import React, { useEffect, useCallback, useState } from 'react';
import {
  Theme, TextField, FormHelperText, createStyles, makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useField, useFormikContext } from 'formik';
import debounce from 'debounce';
import { useId } from '../../hooks';

export type Option = {
  label: string;
  id: number;
};

type Props = {
  hideErrors?: boolean;
  data: Option[] | undefined;
  name: string;
  label: string;
  disabled?: boolean;
  disableClearable?: boolean;
  onSearch?: (search: string) => void;
  noDisappear?: boolean;
};

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  root: {
    width: '100%',
  },
  popper: {
    zIndex: 2147483100,
  },
  input: {},
  labelRoot: {
    '&$focused': {
      color: palette.text.secondary,
    },
  },
  inputUnderline: {
    '&:before': {
      borderBottomColor: palette.input.borderBottomColor,
    },
    '&&:hover:before': {
      borderBottomColor: palette.input.borderBottomColor,
    },
    '&:after': {
      borderBottomColor: palette.menuAction.selected,
    },
    '&$focused:after': {
      borderBottomColor: palette.menuAction.selected,
    },
    '&$disabled:before': {
      borderBottomStyle: 'dashed',
    },
  },
  focused: {},
  hovered: {},
  disabled: {
    color: palette.disabledInput.color,
  },
}), { name: 'FormAutoComplete' });

const FormAutoComplete = ({
  data,
  name,
  label,
  hideErrors = false,
  disabled = false,
  disableClearable = false,
  onSearch = () => {},
  noDisappear = false,
}: Props) => {
  const classes = useStyles();
  const [selectValue, setSelectValue] = useState<null | Option>(null);
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState(data);
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();
  const autoCompleteId = useId('form-input-');
  const inputId = useId('form-input-');
  const errorId = useId('input-error-');
  const [customField, setCustomField] = useState(selectValue?.label);

  useEffect(() => {
    if (parseInt(field.value, 10) !== -1 && options && options.length) {
      const foundOption = options.find((option) => option.id === parseInt(field.value, 10));
      if (!foundOption) {
        setSelectValue(options[0]);
      } else {
        setSelectValue(foundOption);
      }
    }
  }, [options, setFieldValue, name, field.value]);

  useEffect(() => {
    setOptions(data);
  }, [data, setOptions]);

  const onChange = useCallback((event, option: Option) => {
    if (option) {
      // on select
      setFieldValue(name as never, option.id);
      setCustomField(option.label);
    } else {
      // on clear
      setFieldValue(name as never, -1);
      setCustomField('');
    }
  }, [setFieldValue, name, setCustomField]);

  const callbackOnChangeTextField = useCallback(debounce((search: string) => { onSearch(search); }, 600),
    [onSearch]);

  const onChangeTextField = useCallback((event) => {
    callbackOnChangeTextField(event.target.value);
  }, [callbackOnChangeTextField]);

  if (!options && !noDisappear) {
    return null;
  }

  return (
    <Autocomplete
      disableClearable={disableClearable}
      id={autoCompleteId}
      disabled={disabled}
      open={open}
      onOpen={() => { setOpen(true); }}
      onClose={() => { setOpen(false); }}
      getOptionSelected={(option: Option, value: Option) => option.id === value.id}
      getOptionLabel={(option: Option) => option.label}
      options={options}
      value={selectValue}
      classes={{
        root: classes.root,
        input: classes.input,
        focused: classes.focused,
        popper: classes.popper,
      }}
      onChange={onChange}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            value={customField}
            id={inputId}
            label={label}
            fullWidth
            onChange={onChangeTextField}
            InputLabelProps={{
              classes: {
                root: classes.labelRoot,
                focused: classes.focused,
              },
            }}
            InputProps={{
              classes: {
                input: classes.input,
                underline: classes.inputUnderline,
                focused: classes.focused,
                disabled: classes.disabled,
              },
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
          {meta.touched && meta.error && !hideErrors && (
            <FormHelperText error id={errorId}>{meta.error}</FormHelperText>
          )}
        </>
      )}
    />
  );
};

export default FormAutoComplete;
